import React from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { chatSend } from "@/lib/api";
import Mascot, { MASCOT_URL } from "@/components/Mascot";

const SESSION_KEY = "fc_chat_session_id";
function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [messages, setMessages] = React.useState([
    {
      role: "assistant",
      content:
        "Hi there! I'm the Faithfully Clean mascot — happy to help. Ask me about our services, pricing process, or scheduling, or tap the Free Estimate button. How can I help?",
    },
  ]);
  const scroller = React.useRef(null);

  React.useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, open, sending]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const res = await chatSend(getSessionId(), text);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry — I'm having trouble right now. Please call or text us at 407-375-2862 and we'll take care of you!",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher — uses the mascot. Adds a small "tap to chat" hint badge. */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : "Open chat with Faithfully Clean mascot"}
        className="fixed bottom-5 right-5 z-50 group"
        data-testid="chat-launcher"
      >
        <span className="absolute -top-2 -right-2 z-10 bg-[var(--fc-orange)] text-white text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-md">
          {open ? "Close" : "Chat"}
        </span>
        {open ? (
          <span className="w-16 h-16 rounded-full btn-orange grid place-items-center shadow-2xl">
            <X className="w-7 h-7 text-white" />
          </span>
        ) : (
          <span className="block w-24 h-24 bob drop-shadow-[0_10px_18px_rgba(0,0,0,0.35)]">
            <img
              src={MASCOT_URL}
              alt="Faithfully Clean mascot"
              className="w-full h-full object-contain"
              draggable={false}
            />
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-28 right-5 z-50 w-[92vw] max-w-sm bg-white rounded-3xl shadow-2xl border border-blue-100 overflow-hidden flex flex-col"
          style={{ height: "min(620px, 82vh)" }}
          data-testid="chat-panel"
        >
          {/* Header with talking mascot */}
          <div className="relative px-5 py-4 bg-gradient-to-r from-[var(--fc-blue-deep)] to-[var(--fc-blue-dark)] text-white flex items-center gap-3">
            <div className="relative w-14 h-14 flex-shrink-0 drop-shadow-[0_4px_8px_rgba(0,0,0,0.35)]">
              <Mascot className="w-full h-full" talking={sending} />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-lg font-bold">Faithfully Clean Bot</div>
              <div className="text-white/85 text-xs">
                {sending ? "Typing…" : "Friendly help · replies in seconds"}
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="ml-auto p-2 rounded-full hover:bg-white/10"
              aria-label="Close chat"
              data-testid="chat-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scroller}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-[var(--fc-surface)]"
            data-testid="chat-scroll"
          >
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              return (
                <div
                  key={i}
                  className={`flex items-end gap-2 ${isUser ? "justify-end" : "justify-start"}`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {!isUser && (
                    <div className="w-9 h-9 flex-shrink-0">
                      <img src={MASCOT_URL} alt="" className="w-full h-full object-contain" />
                    </div>
                  )}
                  <div
                    className={
                      isUser
                        ? "max-w-[78%] rounded-2xl rounded-br-sm px-4 py-2.5 leading-relaxed text-sm bg-[var(--fc-blue)] text-white shadow-md"
                        : "relative max-w-[78%] rounded-2xl rounded-bl-sm px-4 py-2.5 leading-relaxed text-sm bg-white border border-blue-100 text-slate-800 shadow-sm bubble-tail"
                    }
                  >
                    {m.content}
                  </div>
                </div>
              );
            })}

            {sending && (
              <div className="flex items-end gap-2" data-testid="chat-typing">
                <div className="w-9 h-9 flex-shrink-0">
                  <img src={MASCOT_URL} alt="" className="w-full h-full object-contain mascot-talking" />
                </div>
                <div className="relative bg-white border border-blue-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm bubble-tail">
                  <span className="inline-flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "120ms" }} />
                    <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "240ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={send}
            className="p-3 border-t border-blue-100 bg-white flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask the mascot anything…"
              className="flex-1 h-11 px-4 rounded-full border border-slate-200 outline-none focus:border-[var(--fc-blue)]"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="h-11 w-11 rounded-full btn-orange grid place-items-center disabled:opacity-60"
              data-testid="chat-send"
              aria-label="Send"
            >
              {sending ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Send className="w-4 h-4 text-white" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
